import Hyperbee from 'hyperbee'
import b4a from 'b4a'
import z32 from 'z32'
import {
  Identity, Invite, INVITE_KIND, randomBytes, derive, encrypt, decrypt,
  normalizeUsername, isValidUsername, usernameError
} from '@pine/crypto'
import { ROOM_TYPE, ROLE, KIND, ACTION } from '@pine/protocol'
import { Emitter } from './emitter.js'
import { Room } from './room.js'
import { PairingHost, PairingGuest } from './pairing.js'
import { Inbox, deliverLetter } from './inbox.js'
import { DirectMessages } from './directs.js'
import { UsernameDirectory, lookupUsername } from './usernames.js'
import { cleanName } from './names.js'
import { UsernameRegistryClient } from './registry.js'
import { RoomDirectoryClient, profileTime } from './directory.js'
import { ReportClient, transcriptFrom } from './reports.js'
import { PushClient } from './push.js'
import { Wakes } from './wakes.js'
import { normalizeBotCard } from './bots.js'
import { Assistant } from './assistant.js'
import { Stories } from './stories.js'
import { sendLink, sendSealed, SealedBox } from './linking.js'
import { syncClock, now as clockNow } from './clock.js'

const LOCAL_NAMESPACE = 'pine/local'
const VERIFIED_TTL = 5 * 60 * 1000

/* Key derivation context for the vault. Exactly eight bytes, like the rest. */
const VAULT_CONTEXT = 'pine-vlt'

/* How long a room restored from the vault is given to find its own way to
 * write access before we go and ask the members for it. */
const WRITABLE_GRACE = 5000
/* And how often a restored chat that still cannot be written in asks again. */
const SETTLE_RETRY = [35 * 1000, 60 * 1000, 3 * 60 * 1000, 10 * 60 * 1000]
const SETTLE_GIVE_UP = 24 * 60 * 60 * 1000
/* An unanswered request this old may be replaced by a new one. */
const RESTORE_REASK = 30 * 1000

/* Media fetched as soon as it arrives rather than when somebody looks at it.
 * See _prefetchMedia() for why. */
const PREFETCH_WINDOW = 30
const PREFETCH_INTERVAL = 3000
const PREFETCH_MAX_BYTES = 40 * 1024 * 1024

/*
 * Restoring a chat rides on the inbox rather than adding a protocol.
 *
 * A letter already carries a signed note between two accounts, and these two
 * prefixes are all that is needed to tell a restore request and its answer
 * apart from somebody asking to start a conversation. Keeping the wire format
 * untouched matters: a client one version behind still decodes these letters,
 * and simply ignores what it does not recognise, instead of failing to read
 * ordinary invites from a newer one.
 */
const RESTORE_REQUEST = 'pine:restore/1 '

/*
 * A letter inviting somebody into a group or a channel, rather than starting
 * a one-to-one chat. Marked, because the receiving side used to treat every
 * letter from somebody it already had a chat with as a repeat of that chat's
 * request — and dropped it. A bot asked to join a group by a person it talks
 * to never heard about the group at all.
 */
export const GROUP_INVITE = 'pine:invite/1'

const MEDIA_KINDS = new Set([KIND.IMAGE, KIND.GIF, KIND.VIDEO, KIND.AUDIO])

/* How long a room somebody left is remembered, so no device of theirs brings it back. */
const LEFT_TTL = 90 * 24 * 60 * 60 * 1000

/* A request older than this is dropped: its invite is long dead on the other side. */
const REQUEST_TTL = 7 * 24 * 60 * 60 * 1000

/* How often the server's clock is asked again (see clock.js). A clock does
 * not drift much in half an hour; somebody changing the time zone by hand
 * is caught at the next ask, or when the network comes back. */
const CLOCK_EVERY = 30 * 60 * 1000

/**
 * The whole app in one object.
 *
 * PineClient owns the identity, the transport, the list of rooms and the
 * local index that survives restarts. UI layers (web, desktop, Android) talk
 * only to this — they never touch Autobase or Hyperswarm directly, which is
 * what lets the same UI run over a native DHT socket on desktop and over a
 * WebSocket relay in a browser tab.
 */
export class PineClient extends Emitter {
  constructor ({ transport, identity = null, storage = null, registryUrl = null } = {}) {
    super()
    if (!transport) throw new Error('PineClient needs a transport')

    this.transport = transport
    this.identity = identity
    this.storage = storage
    this.registryUrl = registryUrl

    this.local = null          // Hyperbee: rooms, contacts, settings
    this.rooms = new Map()     // roomId -> Room
    this.summaries = new Map() // roomId -> persisted record
    this.hosts = new Map()     // inviteCode -> PairingHost
    this.inbox = null
    this.usernames = null
    this.requests = []         // pending contact requests
    this._verifiedCache = new Map() // account -> { value: badges[], at }
    this._frozenCache = new Map()   // account -> { value: boolean, at }
    this.selfFrozen = false

    this.opening = null
    this.closing = null
    this.settings = {}
    this.onboarded = false

    this.directory = null
    this._publishTimer = null
    this._restoring = new Set()   // roomId -> a restore request is out
    this.blocked = new Map()      // account -> when it was blocked here
    this._device = null
    this._deviceRegistered = false

    // Set by the app so the session list reads "Pine на Android" rather than
    // a row nobody can tell apart from the others.
    this.deviceName = ''
    this.platform = ''
  }

  /* ------------------------------------------------------------------ */
  /* lifecycle                                                          */
  /* ------------------------------------------------------------------ */

  ready () {
    if (!this.opening) this.opening = this._open()
    return this.opening
  }

  async _open () {
    await this.transport.ready()

    const core = this.transport.store.namespace(LOCAL_NAMESPACE).get({
      name: 'index',
      encryptionKey: derive(this.transport.store.primaryKey, 'pine-loc', 3)
    })
    this.local = new Hyperbee(core, { keyEncoding: 'utf-8', valueEncoding: 'json' })
    await this.local.ready()

    await this._loadIdentity()
    await this._loadSettings()
    await this._loadRooms()
    await this._loadRequests()
    await this._loadBlocked()

    this.inbox = new Inbox(this)
    this.inbox.on('letter', (letter) => this._onLetter(letter))
    await this.inbox.start()

    // Invites for one-to-one chats that have not reached their recipient yet
    // are retried from here, for as long as it takes.
    this.directs = new DirectMessages(this)
    this.directs.on('pending-changed', () => this.emit('rooms-changed'))
    await this.directs.start()

    this.usernames = new UsernameDirectory(this)
    this.registry = new UsernameRegistryClient({
      url: this.registryUrl,
      identity: this.identity
    })
    this.directory = new RoomDirectoryClient({
      url: this.registryUrl,
      identity: this.identity
    })
    this.support = new ReportClient({
      url: this.registryUrl,
      identity: this.identity
    })
    this.push = new PushClient({
      url: this.registryUrl,
      identity: this.identity
    })
    this._pushToken = null
    this.wakes = new Wakes(this)
    // Anya, the assistant: her chat is opened by the app itself. See lib/assistant.js.
    this.assistant = new Assistant(this)
    // A day-long photo or video for everyone this account talks to. See lib/stories.js.
    this.stories = new Stories(this)

    // What time it is by the server's clock, before anything is written:
    // every message is stamped with it (lib/clock.js).
    syncClock(this.registryUrl).catch(() => {})
    this._clockTimer = setInterval(() => syncClock(this.registryUrl).catch(() => {}), CLOCK_EVERY)
    if (this._clockTimer.unref) this._clockTimer.unref()

    // Bring back the chat list of an account restored onto a new device, and
    // keep the server's copy in step with this one. Both in the background:
    // the app opens on what is already on disk.
    this.syncDirectory().catch(() => {})

    // Whether this account is frozen: what was last known at once, the
    // server's answer when it comes, and again every few minutes after.
    const remembered = await this.local.get('frozen/' + this.identity.id).catch(() => null)
    this.selfFrozen = !!(remembered && remembered.value && remembered.value.frozen)
    this.refreshFrozen().catch(() => {})
    this._frozenTimer = setInterval(() => this.refreshFrozen().catch(() => {}), 10 * 60 * 1000)
    if (this._frozenTimer.unref) this._frozenTimer.unref()

    if (this.identity.profile.username) {
      // Announce on the DHT straight away so the handle resolves even if the
      // registry is unreachable, and renew the reservation in the background —
      // claims lapse if nobody comes back for them.
      this.usernames.publish(this.identity.profile.username).catch(() => {})
      this.registry.claim(this.identity.profile.username)
        .then(() => this.emit('username', this.identity.profile.username))
        .catch((err) => {
          // Somebody else holds it now; stop pretending it is ours.
          if (err.reason === 'taken') {
            this.updateProfile({ username: '' }).catch(() => {})
            this.usernames.publish('').catch(() => {})
            this.emit('username-lost', this.identity.profile.username)
          }
        })
    }

    // Her chat, once the app has had a moment to read what the account has.
    this._assistantSoon(4000)
    this.stories.start()

    this.transport.onStatus((status, mode) => {
      this.emit('status', status, mode)
      // Back on the network — after sleep, a dead connection, a tab put away:
      // the same question as at launch, whether anybody has news for us.
      if (status === 'online') {
        this._pullAfter(8000)
        syncClock(this.registryUrl).catch(() => {})
        if (this.stories && Date.now() - this.stories.fetchedAt > 60000) this.stories.refresh().catch(() => {})
      }
    })
    this.emit('ready')
    return this
  }

  /**
   * The network under the app changed — Wi-Fi to mobile data, a VPN switched
   * on or off. Start the link over now rather than wait for the old one to
   * admit it is gone (see Transport.reconnect), and ask the server's clock
   * again while at it: a VPN can land the phone in another time zone's view.
   */
  reconnect () {
    if (this.transport && typeof this.transport.reconnect === 'function') this.transport.reconnect()
    syncClock(this.registryUrl).catch(() => {})
  }

  /** Make sure the link is still carrying something, and mend it if not. */
  async checkConnection () {
    if (this.transport && typeof this.transport.check === 'function') await this.transport.check()
  }

  /**
   * Make sure this device can be found, now: a live link, and every topic
   * announced again (see Transport.reannounce).
   *
   * For the moments a sleeping phone is woken: a push, the KeepAlive alarm.
   * A push used to do nothing at all to an app that was still running. It
   * only started the service, which was already started, while the phone
   * had long dropped off the DHT; the caller kept ringing a phone nobody
   * could find.
   */
  async refreshPresence () {
    await this.checkConnection()
    if (this.transport && typeof this.transport.reannounce === 'function') this.transport.reannounce()
  }

  /**
   * Sign out of this device.
   *
   * There is no server session to end — the account *is* the key held here —
   * so signing out means erasing it along with everything stored under it.
   * The only way back into the same account is the recovery key, which is why
   * the UI makes you confirm you have it before calling this.
   *
   * Room cores are left on disk but become unreadable: their encryption keys
   * lived only in the index this wipes.
   */
  async signOut () {
    /*
     * The chat directory is deliberately left alone.
     *
     * It belongs to the account, not to this device, and signing out is how
     * somebody moves to a new phone — erasing it here meant coming back with
     * the recovery key to a blank list, which is the exact situation the
     * directory exists to prevent. Only forgetAccount() clears it, and only
     * when that is what was asked for.
     */
    /*
     * But it is told the latest before this device forgets everything.
     *
     * Whatever was edited in the last few seconds - a new bio, a chat just
     * joined - would otherwise exist nowhere once the index is erased: the
     * scheduled publish that would have carried it is cancelled right here.
     * Bounded, because nobody should be stuck on the sign-out button waiting
     * for a server that is not answering.
     */
    if (this._publishTimer) {
      clearTimeout(this._publishTimer)
      this._publishTimer = null
    }
    await Promise.race([
      this._publishNow().catch(() => false),
      new Promise((resolve) => setTimeout(resolve, 6000))
    ])

    for (const host of this.hosts.values()) await host.stop().catch(() => {})
    this.hosts.clear()
    if (this.directs) await this.directs.stop().catch(() => {})
    if (this.usernames) await this.usernames.stop().catch(() => {})
    if (this.inbox) await this.inbox.stop().catch(() => {})
    for (const room of this.rooms.values()) await room.close().catch(() => {})
    this.rooms.clear()
    this.summaries.clear()
    this.requests = []
    // The device row was in the index that is about to be erased. Forgetting
    // it here means the next account on this machine mints its own rather than
    // inheriting a row that no longer describes anything.
    this._device = null
    this._deviceRegistered = false
    this.onboarded = false

    // Erase the index: identity, settings, rooms, contacts, invites, outbox.
    const keys = []
    for await (const node of this.local.createReadStream()) keys.push(node.key)
    for (const key of keys) await this.local.del(key).catch(() => {})

    this.emit('signed-out')
    return true
  }

  async close () {
    if (this.closing) return this.closing
    if (this._frozenTimer) clearInterval(this._frozenTimer)
    if (this._clockTimer) clearInterval(this._clockTimer)
    if (this._syncTimer) clearTimeout(this._syncTimer)
    if (this._publishTimer) clearTimeout(this._publishTimer)
    if (this.wakes) this.wakes.close()
    if (this.stories) this.stories.stop()
    if (this._pullTimer) clearTimeout(this._pullTimer)
    if (this._assistantTimer) clearTimeout(this._assistantTimer)
    if (this._settleTimers) for (const timer of this._settleTimers.values()) clearTimeout(timer)
    this.closing = (async () => {
      for (const host of this.hosts.values()) await host.stop().catch(() => {})
      for (const room of this.rooms.values()) await room.close().catch(() => {})
      if (this.directs) await this.directs.stop().catch(() => {})
      if (this.usernames) await this.usernames.stop().catch(() => {})
      if (this.inbox) await this.inbox.stop().catch(() => {})
      if (this.local) await this.local.close().catch(() => {})
      await this.transport.destroy()
      this.removeAllListeners()
    })()
    return this.closing
  }

  /* ------------------------------------------------------------------ */
  /* identity                                                           */
  /* ------------------------------------------------------------------ */

  async _loadIdentity () {
    const node = await this.local.get('identity')

    /*
     * Whether this device has an account is recorded, not guessed.
     *
     * It used to be inferred from the profile having a display name, and
     * restoring from a recovery key does not set one: the app let you in, and
     * the next launch showed the welcome screen again as though the restore had
     * never happened. An account is a keypair on the device; whether its owner
     * has been through the front door is a separate fact, and this is it.
     */
    if (this.identity) {
      this.onboarded = this.onboarded || !!this.identity.profile.name
      await this._persistIdentity()
      return
    }
    if (node) {
      this.identity = new Identity(b4a.from(node.value.seed, 'hex'), node.value.profile || {})

      // A profile from before edit times existed. It is real - somebody typed
      // it - so it gets the smallest possible stamp: enough to outrank a blank
      // card from a device that never had one, and outranked in turn by any
      // edit made from now on.
      const held = this.identity.profile
      if (!profileTime(held.updatedAt) && (held.name || held.bio || held.username || held.avatar)) {
        this.identity.profile = { ...held, updatedAt: 1 }
        await this.local.put('identity', { ...node.value, profile: this.identity.profile })
      }
      // Installs that predate the flag are judged the old way, once.
      this.onboarded = node.value.onboarded !== undefined
        ? !!node.value.onboarded
        : !!this.identity.profile.name
      return
    }
    this.identity = Identity.generate({ name: '' })
    this.onboarded = false
    await this._persistIdentity()
  }

  async _persistIdentity () {
    await this.local.put('identity', {
      seed: b4a.toString(this.identity.seed, 'hex'),
      profile: this.identity.profile,
      onboarded: !!this.onboarded
    })
  }

  /**
   * @param adopted true when the change came from another device through the
   *   chat directory rather than from somebody typing here. An edit is stamped
   *   with the time it was made; an adopted profile keeps the stamp it arrived
   *   with, so taking an old card cannot make it look like a new one.
   */
  async updateProfile (patch, { adopted = false } = {}) {
    const wasOnboarded = this.onboarded
    const next = { ...this.identity.profile, ...patch }
    // Names are pictograph-free everywhere, not only where a screen checked
    // first: this also catches one arriving from an older build, from a linked
    // device, or from the profile a restore brings back. See lib/names.js.
    if (next.name) next.name = cleanName(next.name)
    if (!adopted) next.updatedAt = Date.now()
    this.identity.profile = next
    if (this.identity.profile.name) this.onboarded = true
    await this._persistIdentity()
    this.emit('profile', this.identity.profile)
    // A new account has just been made: Anya's chat belongs in it from the start.
    if (!wasOnboarded && this.onboarded) this._assistantSoon(1500)

    // The server's copy has to follow an edit, not wait for the next time
    // something else happens to publish. Signing out a minute after changing
    // a bio used to lose the bio: nothing had carried it anywhere.
    if (!adopted) this.publishDirectory({ delay: 1500 })

    // The directory hands out a signed card carrying the display name, so it
    // has to re-sign when that name changes.
    if (this.usernames) this.usernames.refresh()

    /*
     * Push the new name and photo at everyone, two ways.
     *
     * Reported from two phones: the name and the picture changed on one and
     * stayed as they were on the other. Both halves of that are here.
     *
     * announceProfile() is live — it goes down the room's signed bus to the
     * peers connected at this moment, and the other side applies it on
     * arrival, so a chat open on both phones changes within the second.
     *
     * announceSelf() is durable, and used to run only over the rooms that
     * happened to be open. A profile is edited from the settings screen, which
     * is the one place in the app where a chat is least likely to be open — so
     * the edit reached whatever was loaded and nothing else, and the chat you
     * had not touched that session went on showing the old name until
     * something else opened it. Every room this account is in is opened for
     * it now.
     */
    const withAvatar = Object.prototype.hasOwnProperty.call(patch, 'avatar')
    for (const room of this.rooms.values()) {
      room.announceSelf().catch(() => {})
      room.announceProfile({ withAvatar })
    }
    if (!adopted) this._announceProfileEverywhere({ withAvatar }).catch(() => {})

    return this.identity.profile
  }

  /**
   * Carry the current profile into rooms that were closed when it changed.
   *
   * Bounded and best-effort: a room that will not open is skipped rather than
   * failing the edit, and the announce on open (see _openRoom) covers it the
   * next time somebody reads the chat.
   */
  async _announceProfileEverywhere ({ limit = 40, withAvatar = true } = {}) {
    const pending = this.listRooms()
      .filter((r) => !r.placeholder && !r.deleted && !this.rooms.has(r.id))
      .slice(0, limit)

    for (const record of pending) {
      const room = await this.openRoom(record.id).catch(() => null)
      if (!room) continue
      await room.announceSelf().catch(() => {})
      room.announceProfile({ withAvatar })
    }
  }

  /**
   * Point everything that holds the account key at the account we now are.
   *
   * _open() builds the inbox, the registry client and the directory client
   * around whatever identity existed at the time — on a fresh install, a
   * throwaway one generated seconds earlier. Restoring replaces this.identity
   * and, until this existed, replaced nothing else: the directory went on
   * signing its requests as the account that had just been signed out of, so
   * the server answered about that account and the chat list came back empty.
   * The handle never reappeared for the same reason, and letters addressed to
   * the restored account arrived on a topic nobody was listening to.
   *
   * Restarting the app fixed all three, because it rebuilt them. That is why
   * the chats "turned up the next day", and this is why they no longer have to.
   */
  async _rebindIdentity () {
    // A publish scheduled for the account this device was a moment ago would
    // otherwise fire as the account it is now - before that account's list
    // and profile have been read back - and overwrite them with a half-loaded
    // snapshot.
    if (this._publishTimer) {
      clearTimeout(this._publishTimer)
      this._publishTimer = null
    }

    if (this.registry) this.registry.identity = this.identity
    if (this.directory) this.directory.identity = this.identity
    if (this.support) this.support.identity = this.identity
    if (this.push) {
      this.push.identity = this.identity
      // This device now wakes for the account it has become. The server moves
      // the token across on registration, so the old account stops ringing
      // on a phone that has signed out of it.
      // The platform goes in the options, as setPushToken() passes it. It was
      // passed bare, and a browser's subscription re-registered here was filed
      // as an Android token — which the server hands to Firebase, not to the
      // browser's push service, so after signing back in nothing arrived.
      if (this._pushToken) this.push.register(this._pushToken, { platform: this._pushPlatform || 'android' }).catch(() => {})
    }

    // Whether the account we are now is frozen has nothing to do with the one
    // we were a moment ago.
    this._frozenCache.clear()
    const remembered = await this.local.get('frozen/' + this.identity.id).catch(() => null)
    const wasFrozen = this.selfFrozen
    this.selfFrozen = !!(remembered && remembered.value && remembered.value.frozen)
    if (wasFrozen !== this.selfFrozen) this.emit('frozen', this.selfFrozen)
    this.refreshFrozen().catch(() => {})

    // The inbox topic is derived from the account id, so a restored account
    // has to listen somewhere else entirely. Rebuilt rather than repointed:
    // the topic is settled in its constructor.
    if (this.inbox) {
      await this.inbox.stop().catch(() => {})
      this.inbox = new Inbox(this)
      this.inbox.on('letter', (letter) => this._onLetter(letter))
      await this.inbox.start().catch(() => {})
    }
    if (this.usernames) this.usernames.refresh()

    // Awaited, not fired and forgotten: the caller is the sign-in screen, and
    // the whole point is that it lands on a list with the chats already in it.
    // The fetch has its own timeout, so this cannot hang the screen for long.
    this._syncDelay = 0
    this._directoryRead = false
    await this.syncDirectory({ attempts: 3 }).catch(() => {})
    // A different account: whether it may talk to Anya, and whether it has a
    // chat with her already, are its own questions.
    if (this.assistant) this.assistant._access = null
    this._assistantSoon(1500)
    if (this.stories) {
      this.stories.items = []
      this.stories.start()
      this.stories.refresh().catch(() => {})
    }
  }

  /**
   * Become the account another device handed over.
   *
   * The seed arrives from lib/linking.js, sealed to a key this device made for
   * the occasion. Same effect as typing a recovery key, minus the typing —
   * and the profile comes with it, so the new device does not start nameless.
   */
  async adoptSeed (seed, profile = {}) {
    const bytes = typeof seed === 'string' ? b4a.from(seed, 'hex') : seed
    if (!bytes || bytes.byteLength !== 32) throw new Error('That is not an account seed')

    const kept = this.identity ? this.identity.profile : {}
    this.identity = new Identity(bytes, {
      ...kept,
      ...Object.fromEntries(Object.entries(profile || {}).filter(([, v]) => v))
    })
    this.onboarded = true
    await this._persistIdentity()
    this.emit('profile', this.identity.profile)
    await this._rebindIdentity()
    return this.identity
  }

  /** Replace the current account. Existing rooms stay on disk but become unreadable. */
  async restoreIdentity (recoveryKey) {
    const identity = Identity.fromRecoveryKey(recoveryKey, this.identity ? this.identity.profile : {})
    this.identity = identity
    // Restoring is getting in. Without this the device forgot by the next
    // launch, and the recovery key had to be typed again every time.
    this.onboarded = true
    await this._persistIdentity()
    this.emit('profile', this.identity.profile)
    await this._rebindIdentity()
    return identity
  }

  /* ------------------------------------------------------------------ */
  /* usernames                                                          */
  /* ------------------------------------------------------------------ */

  get username () {
    return this.identity.profile.username || ''
  }

  /**
   * Claim a handle.
   *
   * The registry decides who holds a name — that is the whole reason it
   * exists, since a DHT announcement cannot be exclusive. Once the claim is
   * accepted the handle is also announced peer to peer, so lookups keep
   * working if the server goes away.
   *
   * @throws Error with `.reason` of 'taken', 'offline', or a shape problem.
   */
  async setUsername (input) {
    const username = normalizeUsername(input)

    if (username) {
      const problem = usernameError(username)
      if (problem) {
        const error = new Error('Invalid username')
        error.reason = problem
        throw error
      }
    }

    const previous = this.username

    if (!username) {
      if (previous) await this.registry.release(previous)
      await this.updateProfile({ username: '' })
      await this.usernames.publish('')
      this.emit('username', '')
      return ''
    }

    // Reserve first: announcing a name we do not hold would send searchers to
    // the wrong account.
    await this.registry.claim(username)
    if (previous && previous !== username) await this.registry.release(previous)

    await this.updateProfile({ username })
    await this.usernames.publish(username)
    this.emit('username', username)
    return username
  }

  /**
   * Take a collectible name the operator issued, from the link it came in.
   *
   * The server binds the name to this account on a signed request — whoever
   * opens the link first and says yes — and it becomes this account's handle
   * like any other, except that it never lapses.
   */
  async collectUsername (token) {
    const result = await this.registry.collect(token)
    const username = normalizeUsername(result.username)
    await this.updateProfile({ username })
    await this.usernames.publish(username).catch(() => {})
    this.emit('username', username)
    return username
  }

  /**
   * Resolve a handle to accounts.
   *
   * The registry answers first and is authoritative. If it cannot be reached,
   * the DHT is asked instead — which can return more than one claimant, so
   * results stay a list and each carries `authoritative` to say which path
   * found it.
   */
  async findUsers (query, opts = {}) {
    const found = await this._findUsers(query, opts)
    if (!found.length) return found
    // The registry already hides a frozen account's name; this covers the
    // other two ways of finding somebody — the DHT, where a frozen device
    // still answers for itself, and the contacts already on this phone.
    const frozen = await this.frozenAmong(found.map((person) => person.account))
    return found.filter((person) => !frozen.has(person.account))
  }

  async _findUsers (query, opts = {}) {
    const username = normalizeUsername(query)
    if (!isValidUsername(username)) return []

    // What this device already knows. Free, instant, and — the point — it
    // works when the person is asleep, which is most of the time.
    const known = await this._knownByUsername(username)

    const registered = await this.registry.lookup(username)
    if (registered) {
      const card = await lookupUsername(this, username, { timeout: 2500 })
        .then((list) => list.find((c) => c.account === registered.account))
        .catch(() => null)

      const mine = known.find((k) => k.account === registered.account)
      return [{
        account: registered.account,
        username,
        name: (card && card.name) || (mine && mine.name) || '',
        bio: (card && card.bio) || '',
        at: registered.since,
        verified: !!registered.verified,
        // The registry answers with these in the same breath as the name, so
        // a search result carries its badges without a second round trip.
        badges: Array.isArray(registered.badges)
          ? registered.badges
          : (registered.verified ? ['verified'] : []),
        authoritative: true
      }]
    }

    /*
     * No registry answer. That is not the same as "no such person".
     *
     * A handle claimed from a build that could not reach the registry — which
     * every Android install was, until the preflight was fixed — lives only as
     * a DHT announcement, and a DHT announcement is only answered while its
     * owner is online. Searching for a friend who had closed the app therefore
     * found nothing at all, with no way to tell that from a typo.
     *
     * So anyone already on this device is offered before falling back to
     * asking the network. Somebody who shares a chat with you is exactly the
     * person you are most likely to be looking for.
     */
    const found = await lookupUsername(this, username, opts)
      .then((list) => list.map((card) => ({ ...card, authoritative: false })))
      .catch(() => [])

    const seen = new Set(found.map((c) => c.account))
    return [...found, ...known.filter((k) => !seen.has(k.account))]
  }

  /**
   * Accounts on this device that go by this handle.
   *
   * Two sources, both already here: the contact cards, and the member lists
   * the chat directory publishes alongside each room — which carry a handle
   * per account precisely so a restored device can show something friendlier
   * than a key.
   */
  async _knownByUsername (username) {
    const out = new Map()

    const contacts = await this.listContacts().catch(() => [])
    for (const contact of contacts) {
      if (normalizeUsername(contact.username || '') !== username) continue
      out.set(contact.account, {
        account: contact.account,
        username,
        name: contact.name || '',
        bio: '',
        at: contact.at || 0,
        verified: false,
        badges: [],
        authoritative: false,
        known: true
      })
    }

    for (const record of this.summaries.values()) {
      for (const member of record.members || []) {
        if (!member || !member.account || out.has(member.account)) continue
        if (member.account === this.identity.id) continue
        if (normalizeUsername(member.username || '') !== username) continue
        out.set(member.account, {
          account: member.account,
          username,
          name: (record.peer === member.account && record.name) || '',
          bio: '',
          at: 0,
          verified: false,
          badges: [],
          authoritative: false,
          known: true
        })
      }
    }

    return [...out.values()]
  }

  /**
   * Whether an account carries the operator's verified badge.
   *
   * Cached for a few minutes: it changes rarely and the UI asks about the same
   * handful of accounts repeatedly. A registry answer of "not verified" is
   * cached too, so a plain account does not re-hit the server on every render;
   * a network failure is not, so it retries once the registry is back.
   */
  async isVerified (account) {
    return (await this.badgesFor(account)).includes('verified')
  }

  /**
   * Every badge an account carries: 'verified', 'tester', 'donor',
   * 'translator'.
   *
   * Cached for a few minutes, an empty answer included, because the UI asks
   * about the same handful of accounts on every render and most of them carry
   * nothing. A failure to reach the registry is not cached — the badges
   * already on screen stay, and the next render tries again.
   *
   * Asking about several at once collapses a list into one request; the cache
   * is filled for all of them either way.
   */
  async badgesFor (account) {
    if (!account) return []
    const hit = this._verifiedCache.get(account)
    if (hit && Date.now() - hit.at < VERIFIED_TTL) return hit.value

    const answer = await this.badgesForAll([account])
    return answer[account] || (hit ? hit.value : [])
  }

  /**
   * Our own badges and which of them we show.
   *
   * Everyone else sees only the visible set (the server answers with that and
   * nothing more), so this signed round trip is the only way the settings
   * screen can offer a switch per badge.
   */
  async myBadges () {
    const answer = await this.registry.badgeVisibility(null)
    return answer && !answer.error ? answer : { granted: [], hidden: [], visible: [] }
  }

  /**
   * @returns the new state, `{ error }` when the server refused, or null when
   *   it could not be reached — three different things the screen says
   *   differently.
   */
  async setHiddenBadges (hidden) {
    const answer = await this.registry.badgeVisibility(Array.isArray(hidden) ? hidden : [])
    // Our own entry in the shared cache is now wrong — it holds what the
    // *others* were told, and that has just changed.
    this._verifiedCache.delete(this.identity.id)
    return answer
  }

  /* ------------------------------------------------------------------ */
  /* freezing                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Of these accounts, the ones the operator has frozen.
   *
   * A frozen account keeps every chat it already has and loses the ability
   * to start new ones — in either direction: it cannot open a chat, and
   * nobody can open one with it. The server decides who is frozen; every
   * client enforces it, the frozen one's included, because in a network with
   * no server in the middle of a conversation there is nowhere else to.
   *
   * Cached for a few minutes. Offline, the last answer stands, and an
   * account never asked about is treated as not frozen: a messenger that
   * refuses every new chat because its server is briefly unreachable has
   * broken the one thing it promised not to depend on the server for.
   */
  async frozenAmong (accounts) {
    const now = Date.now()
    const out = new Set()
    const ask = []

    for (const account of new Set((accounts || []).filter(Boolean))) {
      const hit = this._frozenCache.get(account)
      if (hit && now - hit.at < VERIFIED_TTL) {
        if (hit.value) out.add(account)
      } else {
        ask.push(account)
      }
    }
    if (!ask.length) return out

    const answer = await this.registry.frozen(ask).catch(() => null)
    if (answer === null) {
      for (const account of ask) {
        const hit = this._frozenCache.get(account)
        if (hit && hit.value) out.add(account)
      }
      return out
    }

    const frozen = new Set(answer)
    for (const account of ask) {
      this._frozenCache.set(account, { value: frozen.has(account), at: now })
      if (frozen.has(account)) out.add(account)
    }
    return out
  }

  async isFrozen (account) {
    return (await this.frozenAmong([account])).has(account)
  }

  /**
   * Whether this account is frozen, asked again and remembered on disk.
   *
   * Remembered, because the answer has to survive a phone that is offline:
   * being frozen yesterday and unreachable today does not make an account
   * less frozen. Emits 'frozen' when it changes, which is what puts the
   * notice at the top of the chat list up — and takes it down.
   */
  async refreshFrozen () {
    if (!this.identity) return this.selfFrozen
    this._frozenCache.delete(this.identity.id)
    const answer = await this.registry.frozen([this.identity.id]).catch(() => null)
    if (answer === null) {
      // Could not ask. Keep the last answer for half a minute rather than
      // asking again on every button press: against a server that is slow
      // rather than down, each question costs its full timeout, and starting a
      // chat must not hang on that.
      this._frozenCache.set(this.identity.id, {
        value: this.selfFrozen,
        at: Date.now() - VERIFIED_TTL + 30 * 1000
      })
      return this.selfFrozen
    }

    const frozen = answer.includes(this.identity.id)
    this._frozenCache.set(this.identity.id, { value: frozen, at: Date.now() })
    if (frozen !== this.selfFrozen) {
      this.selfFrozen = frozen
      // Keyed by account: the device may be signed into another one tomorrow.
      await this.local.put('frozen/' + this.identity.id, { frozen, at: Date.now() }).catch(() => {})
      this.emit('frozen', frozen)
    }
    return frozen
  }

  /** Throws when this account may not start a new chat. */
  async _assertMayStartChats () {
    // Asked fresh when the cache has gone stale, so a freeze lifted a minute
    // ago does not keep somebody locked out for the rest of the session.
    const hit = this._frozenCache.get(this.identity.id)
    const frozen = hit && Date.now() - hit.at < VERIFIED_TTL
      ? hit.value
      : await this.refreshFrozen()
    if (frozen) {
      const error = new Error('This account is frozen and cannot start new chats')
      error.reason = 'frozen'
      throw error
    }
  }

  /** Throws when a new chat with this account is not allowed. */
  async _assertReachable (account) {
    if (await this.isFrozen(account)) {
      const error = new Error('That account is frozen')
      error.reason = 'peer-frozen'
      throw error
    }
  }

  async badgesForAll (accounts) {
    const now = Date.now()
    const out = {}
    const ask = []

    for (const account of new Set((accounts || []).filter(Boolean))) {
      const hit = this._verifiedCache.get(account)
      if (hit && now - hit.at < VERIFIED_TTL) {
        if (hit.value.length) out[account] = hit.value
      } else {
        ask.push(account)
      }
    }
    if (!ask.length) return out

    const answer = await this.registry.badges(ask)
    if (answer === null) {
      // Offline. Hand back whatever was last known rather than saying that
      // nobody has a badge, which would flicker every mark off the screen.
      for (const account of ask) {
        const hit = this._verifiedCache.get(account)
        if (hit && hit.value.length) out[account] = hit.value
      }
      return out
    }

    for (const account of ask) {
      const value = Array.isArray(answer[account]) ? answer[account] : []
      this._verifiedCache.set(account, { value, at: now })
      if (value.length) out[account] = value
    }
    return out
  }

  /* ------------------------------------------------------------------ */
  /* the chat directory                                                 */
  /* ------------------------------------------------------------------ */

  /**
   * What this device believes it is a member of.
   *
   * Members come from the room itself when it is open, and from the last
   * reading otherwise — so a list published while most chats are closed does
   * not quietly shrink to the handful that happen to be loaded. Notes to self
   * are left out: nobody else is in them and nothing can restore them.
   */
  async _roomSnapshot () {
    const out = []
    for (const record of this.summaries.values()) {
      if (record.deleted || record.placeholder) continue
      if (record.type === ROOM_TYPE.SAVED) continue

      let members = Array.isArray(record.members) ? record.members : []
      const room = this.rooms.get(record.id)
      if (room) {
        const list = await room.view.members().catch(() => null)
        if (list) {
          members = await Promise.all(list.map(async (m) => ({
            account: m.account,
            username: await this._usernameFor(m.account)
          })))
          // Cache it, so the next publish still knows who is here when the
          // room is closed.
          if (JSON.stringify(members) !== JSON.stringify(record.members)) {
            await this._saveSummary({ ...this.summaries.get(record.id), members })
          }
        }
      }

      out.push({
        id: record.id,
        name: record.type === ROOM_TYPE.DM ? '' : (record.name || ''),
        type: record.type ?? ROOM_TYPE.GROUP,
        members
      })
    }
    return out
  }

  /**
   * A handle for an account, if we happen to know one.
   *
   * Only from what is already on this device — the point of publishing them is
   * that a restored device can show something friendlier than a 52-character
   * id, not to make the server resolve anything.
   */
  async _usernameFor (account) {
    if (account === this.identity.id) return this.identity.profile.username || this.username || ''
    const contact = await this.getContact(account).catch(() => null)
    return (contact && contact.username) || ''
  }

  /**
   * The part of a profile that travels with the account.
   *
   * Name, bio, handle and photo — the fields somebody typed about themselves,
   * which every room they are in already carries. The photo is the large one
   * by a wide margin, but it was the field people noticed missing: a restored
   * device that knows your name and shows a blank circle does not look like it
   * restored anything. It is a 256px JPEG, capped on both sides.
   */
  _profileCard () {
    const profile = this.identity.profile || {}
    const card = {
      name: profile.name || '',
      bio: profile.bio || '',
      username: profile.username || this.username || '',
      avatar: typeof profile.avatar === 'string' ? profile.avatar : '',
      updatedAt: profileTime(profile.updatedAt)
    }
    // A card with nothing on it says nothing, so it is not sent. It used to
    // be, and a device that had never had a profile - a laptop signed in with
    // a recovery key by a build that did not bring the profile back - erased
    // the real one for every device of the account.
    if (!card.name && !card.bio && !card.username && !card.avatar) return null
    return card
  }

  /**
   * The room keys, sealed for this account and for nobody else.
   *
   * A room is only openable with two 32-byte keys, and until now they lived on
   * exactly one device. Losing it meant the chat could only come back if
   * somebody else in it was online to hand over an invite — which, in a
   * conversation between two people, means waiting for your friend.
   *
   * So they are backed up, encrypted under a key derived from the account seed
   * and posted with the rest of the directory. The server stores a string it
   * cannot read: the seed is what opens it, and the seed is never sent
   * anywhere. Anyone already holding that seed *is* the account and could ask
   * the members for the same keys, so this is a change in convenience rather
   * than in who can eventually read a room — but it does put ciphertext on a
   * server that was not there before, which is why the switch below exists.
   *
   * @returns the sealed string; '' when backup is off, which is what erases
   *   the stored copy; null when there is simply nothing to say, so a device
   *   that has not loaded its rooms yet cannot wipe another device's backup.
   */
  async _sealVault () {
    if (this.settings.backupKeys === false) return ''
    const entries = this._vaultEntries()
    if (entries.length === 0) return null
    return this._seal(entries)
  }

  _seal (entries) {
    const sealed = encrypt(b4a.from(JSON.stringify(entries), 'utf8'), this._vaultKey())
    return b4a.toString(sealed, 'base64')
  }

  /** The keys of every room on this device, as the vault holds them. */
  _vaultEntries () {
    const entries = []
    for (const record of this.summaries.values()) {
      if (record.deleted || record.placeholder) continue
      if (record.type === ROOM_TYPE.SAVED) continue
      if (!record.key || !record.encryptionKey) continue
      entries.push({
        id: record.id,
        key: record.key,
        encryptionKey: record.encryptionKey,
        type: record.type ?? ROOM_TYPE.GROUP,
        // The name of a one-to-one chat is the other person's, which is why it
        // is deliberately absent from the plaintext list. In here it is safe,
        // and it is what makes a restored chat show who it is with.
        name: record.name || '',
        peer: record.peer || ''
      })
    }
    return entries
  }

  _openVault (sealed) {
    if (!sealed) return []
    try {
      const plain = decrypt(b4a.from(String(sealed), 'base64'), this._vaultKey())
      if (!plain) return []
      const list = JSON.parse(b4a.toString(plain, 'utf8'))
      return Array.isArray(list) ? list : []
    } catch {
      // A vault written by an account this device is no longer, or a truncated
      // one. Either way there is nothing to be done with it and the plain list
      // still restores the names.
      return []
    }
  }

  _vaultKey () {
    return derive(this.identity.seed, VAULT_CONTEXT, 9)
  }

  /**
   * This device's identity in the session list.
   *
   * A random id minted once and kept on disk, so a device stays the same row
   * across restarts. It says nothing about the machine beyond what the person
   * needs to recognise it in a list.
   */
  async deviceRecord () {
    if (this._device) return this._device
    const node = await this.local.get('device')
    if (node && node.value && node.value.id) {
      this._device = node.value
      return this._device
    }
    const record = {
      id: b4a.toString(randomBytes(16), 'hex'),
      name: this.deviceName || '',
      platform: this.platform || ''
    }
    await this.local.put('device', record)
    this._device = record
    return record
  }

  /** Rename this device, or say what it is. Shown in the session list. */
  async describeDevice ({ name = null, platform = null } = {}) {
    const record = await this.deviceRecord()
    if (name !== null) record.name = String(name).slice(0, 64)
    if (platform !== null) record.platform = String(platform).slice(0, 24)
    await this.local.put('device', record)
    this._device = record
    this.publishDirectory({ delay: 1000 })
    return record
  }

  /**
   * Hand this account to a device showing a link code.
   *
   * The dangerous direction, and the one the UI must ask about first: whoever
   * holds that code ends up holding the account. See lib/linking.js.
   */
  async linkDevice (code) {
    await sendLink(this, code)
    // The new device will register itself; refresh the list on this one.
    this.publishDirectory({ delay: 2000 })
    return true
  }

  /** Every device signed into this account, most recent first. */
  async listDevices () {
    if (!this.directory || !this.directory.enabled) return []
    const stored = await this.directory.fetch()
    if (!stored) return []
    const mine = await this.deviceRecord()
    return stored.devices.map((d) => ({ ...d, current: d.id === mine.id }))
  }

  /**
   * Unlink a device.
   *
   * It stops appearing in the list, and an honest client signs itself out the
   * next time it checks. It is not a lock: a linked device holds the account
   * seed, and only changing the account takes that back. The UI says so.
   */
  async revokeDevice (deviceId) {
    if (!this.directory || !this.directory.enabled) return false
    const mine = await this.deviceRecord()
    if (deviceId === mine.id) return this.signOut()
    return this.directory.revoke(deviceId)
  }

  /** Push the current list to the server, coalescing bursts of changes. */
  publishDirectory ({ delay = 4000 } = {}) {
    if (!this.directory || !this.directory.enabled) return
    if (this._publishTimer) clearTimeout(this._publishTimer)
    this._publishTimer = setTimeout(() => {
      this._publishTimer = null
      this._publishNow().catch(() => {})
    }, delay)
    if (this._publishTimer.unref) this._publishTimer.unref()
  }

  /**
   * Put this device's rooms into the account's list — without taking anybody
   * else's out.
   *
   * The server replaces the list with whatever it is sent, and every device
   * of an account publishes. So a device that knew about fewer rooms — a
   * phone signed in a minute ago, or one whose first read of the list had
   * failed — used to overwrite the whole list with its own short one, and the
   * next restore anywhere brought back only that. The worst case was the
   * common one: a sign-in whose read hit a rate limit went on, eight seconds
   * later, to publish an empty list, and every chat of the account was gone
   * from the server for good.
   *
   * So the list is read first and this device's rooms are added to it, keys
   * included; a room goes from the list only when a device of the account
   * actually left or deleted it (see _leftRooms). If the list cannot be read,
   * nothing is written — the next attempt will do both.
   */
  async _publishNow () {
    if (!this.directory || !this.directory.enabled) return false
    if (!this.onboarded) return false

    const stored = await this.directory.fetch()
    if (!stored) {
      this.publishDirectory({ delay: 30000 })
      return false
    }

    const [local, device, left] = await Promise.all([
      this._roomSnapshot(), this.deviceRecord(), this._leftRooms()
    ])
    const held = this._openVault(stored.vault)
    for (const entry of held) {
      if (entry && entry.left && entry.id && !left.has(entry.id)) left.set(entry.id, entry.at || 0)
    }
    const mine = new Set(local.map((r) => r.id))
    // Rooms this device holds right now are never "gone", whatever another
    // device said: it is in them.
    const gone = (id) => left.has(id) && !mine.has(id) && !this.summaries.has(id)

    const rooms = [...local]
    for (const entry of stored.rooms || []) {
      if (entry && entry.id && !mine.has(entry.id) && !gone(entry.id)) rooms.push(entry)
    }

    let vault = ''
    if (this.settings.backupKeys !== false) {
      const entries = this._vaultEntries()
      const have = new Set(entries.map((e) => e.id))
      for (const entry of held) {
        if (!entry || !entry.id || entry.left || have.has(entry.id) || gone(entry.id)) continue
        if (entry.key && entry.encryptionKey) {
          entries.push(entry)
          have.add(entry.id)
        }
      }
      // Remembered for a while, so every device of the account learns of it.
      const cutoff = Date.now() - LEFT_TTL
      const marks = [...left.entries()]
        .filter(([id, at]) => at > cutoff && gone(id))
        .sort((a, b) => b[1] - a[1])
        .slice(0, 200)
        .map(([id, at]) => ({ id, left: true, at }))
      vault = entries.length || marks.length ? this._seal([...entries, ...marks]) : null
    }

    const ok = await this.directory.publish(rooms, this._profileCard(), device, vault)
    if (ok) this._deviceRegistered = true
    return ok
  }

  /** Rooms this device left or deleted, and when — so a merge does not bring them back. */
  async _leftRooms () {
    const out = new Map()
    for await (const node of this.local.createReadStream({ gte: 'left/', lt: 'left0' })) {
      out.set(node.key.slice('left/'.length), (node.value && node.value.at) || 0)
    }
    return out
  }

  /**
   * Fetch the account's rooms and put back the ones this device does not have.
   *
   * Most arrive complete: the vault carries their keys, so they are written in
   * as real rooms and opened straight away — the list is a list of chats, not
   * of names, and typing in one works.
   *
   * What the vault has no entry for still arrives as a placeholder: a title
   * and a member list and nothing to open. That is what a chat published by a
   * build older than the vault looks like, and by an account that has key
   * backup switched off. restoreRoom() is the way back for those — it asks the
   * people in the room rather than the server.
   */
  async syncDirectory ({ attempts = 1 } = {}) {
    if (!this.directory || !this.directory.enabled) return { restored: 0 }
    // An account nobody has signed into yet — the throwaway a fresh install
    // starts with — has nothing on the server, and every one of them that
    // asked used up the relay's allowance for new accounts from this address.
    if (!this.onboarded) return { restored: 0 }

    let stored = null
    for (let i = 0; i < attempts && !stored; i++) {
      if (i) await new Promise((resolve) => setTimeout(resolve, 2500 * i))
      stored = await this.directory.fetch()
    }
    if (!stored) {
      // Offline, rate limited, or no server. Nothing is written on the back
      // of a read that failed (see _publishNow); the read is simply tried
      // again, sooner than "next launch" — a sign-in that met a busy server
      // used to show an empty list until the app was restarted.
      this._retrySync()
      return { restored: 0, failed: true }
    }
    this._syncDelay = 0
    // What the account already has is known now; see lib/assistant.js for
    // what waits on this.
    this._directoryRead = true

    await this._adoptProfile(stored.profile)

    /*
     * Somebody unlinked this device from another one.
     *
     * Only acted on once this device is known to have registered: a device
     * that has never published is simply absent from a list it was never in,
     * and signing out on that would lock out every new device on its first
     * launch.
     */
    if (this._deviceRegistered && stored.devices.length > 0) {
      const mine = await this.deviceRecord()
      if (!stored.devices.some((d) => d.id === mine.id)) {
        this.emit('revoked')
        await this.signOut().catch(() => {})
        return { restored: 0, revoked: true }
      }
    }

    const vault = new Map()
    const left = await this._leftRooms()
    for (const held of this._openVault(stored.vault)) {
      if (held && held.left && held.id) left.set(held.id, held.at || 0)
      else if (held && held.id && held.key && held.encryptionKey) vault.set(held.id, held)
    }

    // The plain list, and then whatever the vault holds that the list lost —
    // an earlier build could publish a list shorter than its vault.
    const listed = new Set()
    const wanted = []
    for (const entry of stored.rooms || []) {
      if (!entry || !entry.id) continue
      listed.add(entry.id)
      wanted.push(entry)
    }
    for (const held of vault.values()) {
      if (!listed.has(held.id)) wanted.push({ id: held.id, name: '', type: held.type, members: [] })
    }

    let added = 0
    const live = []

    for (const entry of wanted) {
      if (!entry || !entry.id) continue
      if (left.has(entry.id) && !this.summaries.has(entry.id)) continue

      const known = this.summaries.get(entry.id)
      // A room this device already holds the keys for needs nothing from here.
      // A placeholder does, and is upgraded in place if the vault has it.
      if (known && !known.placeholder) continue

      const keys = vault.get(entry.id)
      const members = Array.isArray(entry.members) ? entry.members : []
      const type = entry.type ?? (keys ? keys.type : ROOM_TYPE.GROUP)
      const peer = (keys && keys.peer) || (known && known.peer) ||
        soleOther(type, members, this.identity.id)

      await this._saveSummary({
        pinned: false,
        muted: false,
        archived: false,
        unread: 0,
        ...(known || {}),
        id: entry.id,
        key: keys ? keys.key : null,
        encryptionKey: keys ? keys.encryptionKey : null,
        placeholder: !keys,
        members,
        type,
        // The plain list carries no name for a one-to-one chat on purpose —
        // that name is the other person's. The vault is where it comes back.
        name: entry.name || (keys && keys.name) || (known && known.name) || '',
        peer: peer || undefined,
        joinedAt: (known && known.joinedAt) || clockNow(),
        lastActivity: (known && known.lastActivity) || clockNow()
      })

      if (keys) live.push(entry.id)
      if (!known) added++
    }

    if (added || live.length) this.emit('rooms-changed')

    // Open what came back with keys. Not awaited: a room opening is a swarm
    // join and a replication, and the list should already be on screen by the
    // time any of it finishes. One at a time, because the tail of it may send
    // a letter to every member of every room, and twenty of those at once is
    // a burst nobody asked for.
    if (live.length) {
      this._settleAll(live).catch(() => {})
    }

    this.publishDirectory({ delay: 8000 })
    return { restored: added, live: live.length }
  }

  _retrySync () {
    if (this._syncTimer || this.closing) return
    this._syncDelay = Math.min(5 * 60 * 1000, (this._syncDelay || 5000) * 2)
    this._syncTimer = setTimeout(() => {
      this._syncTimer = null
      this.syncDirectory().catch(() => {})
    }, this._syncDelay)
    if (this._syncTimer.unref) this._syncTimer.unref()
  }

  /**
   * A room that came back from the vault, once it is open.
   *
   * Readable immediately — that is what the keys are for. Writable is a
   * separate question, and the answer depends on which device this is. Signing
   * out and back in on the same machine leaves this device's writer core where
   * it was, still authorised, so it can type at once. A *different* device
   * arrives with a core nobody in the room has ever heard of, and only a
   * member can authorise it; asking for an invite and redeeming it is what
   * does that.
   *
   * So: give it a moment to turn out to be writable on its own, and only then
   * go and ask. Quietly — the chat is already usable, and this only decides
   * whether it can be typed in.
   */
  async _settleAll (roomIds) {
    for (const roomId of roomIds) {
      const room = await this.openRoom(roomId).catch(() => null)
      if (room) await this._settleRestored(room).catch(() => {})
    }
  }

  async _settleRestored (room) {
    if (!room || room.writable) return
    if (await room.waitForWritable({ timeout: WRITABLE_GRACE })) return
    await this.restoreRoom(room.id).catch(() => {})
    this._settleLater(room.id, 0, Date.now())
  }

  /*
   * And ask again until somebody answers.
   *
   * The request goes to whoever is online at that moment, and it went once:
   * a member whose app was closed — or a bot whose machine was busy finding
   * its way onto the DHT — missed it, and the chat sat on "waiting for
   * access" for good, with nothing on screen to try again. Now it keeps
   * asking, less and less often, for a day.
   */
  _settleLater (roomId, attempt, since) {
    if (this.closing || Date.now() - since > SETTLE_GIVE_UP) return
    if (!this._settleTimers) this._settleTimers = new Map()
    if (this._settleTimers.has(roomId)) return
    const delay = SETTLE_RETRY[Math.min(attempt, SETTLE_RETRY.length - 1)]
    const timer = setTimeout(async () => {
      this._settleTimers.delete(roomId)
      const room = this.rooms.get(roomId)
      if (this.closing || !room || room.closed || room.writable || !this.summaries.has(roomId)) return
      await this.restoreRoom(roomId, { again: true }).catch(() => {})
      this._settleLater(roomId, attempt + 1, since)
    }, delay)
    if (timer.unref) timer.unref()
    this._settleTimers.set(roomId, timer)
  }

  /**
   * Take back a name, bio, handle and photo this device does not have.
   *
   * Only the empty fields. A restored device starts blank and should look like
   * its owner again without being asked to retype anything; a device that
   * already has a name has a more recent one than the server does, and
   * overwriting it would undo an edit made while offline.
   */
  async _adoptProfile (stored) {
    if (!stored) return false
    const profile = this.identity.profile || {}
    const fields = ['name', 'bio', 'username', 'avatar']
    const blank = (field) => (field === 'avatar' ? null : '')
    const theirs = profileTime(stored.updatedAt)
    const ours = profileTime(profile.updatedAt)
    const patch = {}

    if (theirs > ours) {
      // Edited on another device after anything this one knows about: take
      // it whole, blanks included - clearing a bio over there is an edit too.
      for (const field of fields) {
        const value = stored[field] || blank(field)
        if ((profile[field] || blank(field)) !== value) patch[field] = value
      }
      patch.updatedAt = theirs
    } else {
      // Otherwise only what this device is missing. A restored device starts
      // blank and should look like its owner without retyping anything; a
      // device with its own newer edit keeps it.
      for (const field of fields) {
        if (!profile[field] && stored[field]) patch[field] = stored[field]
      }
    }

    if (!fields.some((field) => field in patch)) {
      // Same content, newer stamp: remember the stamp so this device's copy
      // is not the one that looks out of date next time.
      if (patch.updatedAt) await this.updateProfile({ updatedAt: patch.updatedAt }, { adopted: true })
      return false
    }

    await this.updateProfile(patch, { adopted: true })

    // The handle is only ours while the registry says so; re-claiming renews
    // the reservation and proves it on the new device.
    if (patch.username && this.registry && this.registry.enabled) {
      this.registry.claim(patch.username).catch(() => {})
      this.usernames.publish(patch.username).catch(() => {})
    }
    return true
  }

  /**
   * Ask the other members for a chat this device has the name of but not the
   * key.
   *
   * The server cannot help here and is not asked: it has never held a room
   * key. What it holds is who else is in the room, and each of those people
   * can mint an invite. So the request goes to them over the inbox, and
   * whoever is online first answers with one.
   *
   * @returns { asked } — how many people the request reached. Zero is not an
   * error; it means nobody was online, and the request can be repeated.
   */
  async restoreRoom (roomId, { again = false } = {}) {
    const record = this.summaries.get(roomId)
    if (!record || record.deleted) throw new Error('Unknown chat: ' + roomId)
    if (!this._restoreAsks) this._restoreAsks = new Map()
    if (this._restoring.has(roomId)) {
      /*
       * One request at a time — unless it is `again` and the one out there
       * has had its chance. A request counts as "asked" as soon as any device
       * took the letter, and this account's own inbox always does; when the
       * member who could actually answer was offline, the request then sat
       * out its full wait with nobody to reply, blocking the next try.
       */
      const previous = this._restoreAsks.get(roomId)
      if (!again || !previous || Date.now() - previous.at < RESTORE_REASK) return { asked: 0, pending: true }
      this._restoreAsks.delete(roomId)
      this._restoring.delete(roomId)
      previous.box.cancel()
    }

    /*
     * Not only for placeholders any more.
     *
     * A chat restored from the vault opens and reads without asking anybody,
     * but on a device that is new to the room its writer core is one no member
     * has authorised — it can be read and not written in. Redeeming a fresh
     * invite is what authorises it, and that is the same request and the same
     * reply as fetching a key. So this button covers both, and does nothing
     * when there is nothing to fix.
     */
    const open = this.rooms.get(roomId)
    if (open && open.writable) return { asked: 0, already: true }

    // Prefer the room's own membership when we have it: the list from the
    // directory is a snapshot from whenever this account last published.
    let members = Array.isArray(record.members) ? record.members : []
    if (open) {
      const live = await open.view.members().catch(() => null)
      if (live && live.length) members = live
    }

    /*
     * This account's other devices are asked as well, not only the other
     * members. They hold the same room and can let this device in just the
     * same — and a computer left running is often the one thing online when a
     * phone signs in, while the person on the other end of a chat is asleep.
     */
    const peers = [this.identity.id, ...members
      .map((m) => m.account)
      .filter((account) => account && account !== this.identity.id)]

    /*
     * The reply has to come back to *this* device, not to this account.
     *
     * Every device signed into an account listens on the same inbox topic, so
     * an invite addressed to the account can be answered by the old phone that
     * still has the room — which then discards it, because it has nothing to
     * restore. The chat stayed a placeholder no matter how many times the
     * button was pressed. A sealed box keyed to this device is an address only
     * one machine holds.
     */
    const box = new SealedBox(this.transport)
    const waiting = box.start({ timeout: 90000 })
    const ask = { box, at: Date.now() }
    // A request replaced by a newer one must not clear the newer one's state
    // when it is cancelled.
    const current = () => this._restoreAsks.get(roomId) === ask
    const settle = () => {
      if (!current()) return
      this._restoreAsks.delete(roomId)
      this._restoring.delete(roomId)
      this.emit('rooms-changed')
    }

    this._restoreAsks.set(roomId, ask)
    this._restoring.add(roomId)
    this.emit('rooms-changed')

    let asked = 0
    await Promise.all(peers.map(async (account) => {
      try {
        await deliverLetter(this, account,
          { invite: '', note: RESTORE_REQUEST + roomId + ' ' + box.code },
          { timeout: 15000 })
        asked++
      } catch { /* not online; another member may be */ }
    }))

    if (asked === 0) {
      settle()
      box.cancel()
      return { asked: 0 }
    }

    waiting
      .then((body) => {
        if (!body) return settle()
        // A reply is worth taking even from a request since replaced.
        return this._onRestoreReply(roomId, body, settle)
      })
      .catch(settle)

    this.emit('rooms-changed')
    return { asked }
  }

  /** Whether a restore request for this chat is still outstanding. */
  isRestoring (roomId) {
    return this._restoring.has(roomId)
  }

  /**
   * Erase what the server holds for this account: the chat list and the
   * profile card. Separate from signing out, because leaving a device is not
   * the same as wanting to be forgotten.
   */
  async forgetAccount () {
    if (!this.directory) return false
    return this.directory.forget()
  }

  /* ------------------------------------------------------------------ */
  /* settings                                                           */
  /* ------------------------------------------------------------------ */

  async _loadSettings () {
    const node = await this.local.get('settings')
    const saved = (node && node.value) || {}
    this.settings = {
      theme: 'system',
      notifications: true,
      notificationSound: true,
      enterToSend: true,
      autoDownloadMedia: true,
      language: 'ru',
      readReceipts: true,
      typingIndicators: true,
      // A card for the first link in a message, made through the server —
      // see packages/pine-ui/lib/link-preview.js for what it learns.
      linkPreviews: true,
      // Back up the room keys, sealed, so a restored device has its chats
      // rather than a list of their names. See _sealVault() for the trade.
      backupKeys: true,
      ...saved
    }

    // Which name server to use is not a setting.
    //
    // It used to be one, and a device that had ever saved an empty value kept
    // that empty value forever — the name server was then reachable from one
    // machine and dead from another running identical code, with nothing in
    // the UI to explain it. Infrastructure the build already knows about does
    // not belong in a per-device store that can outlive and shadow it, so any
    // value left over from that version is dropped here.
    if ('registryUrl' in this.settings) {
      delete this.settings.registryUrl
      await this.local.put('settings', this.settings)
    }
  }

  async setSettings (patch) {
    if ('registryUrl' in patch) {
      patch = { ...patch }
      delete patch.registryUrl
    }
    const backupWas = this.settings.backupKeys
    this.settings = { ...this.settings, ...patch }
    await this.local.put('settings', this.settings)
    this.emit('settings', this.settings)

    // Switching key backup off has to reach the server to mean anything: what
    // is already stored there stays until a publish replaces it, and a publish
    // is normally only triggered by the chat list changing.
    if (backupWas !== this.settings.backupKeys) this.publishDirectory({ delay: 200 })
    return this.settings
  }

  /* ------------------------------------------------------------------ */
  /* rooms                                                              */
  /* ------------------------------------------------------------------ */

  async _loadRooms () {
    for await (const node of this.local.createReadStream({ gte: 'room/', lt: 'room0' })) {
      this.summaries.set(node.value.id, node.value)
    }
  }

  listRooms () {
    return [...this.summaries.values()]
      .filter((r) => !r.deleted)
      .sort((a, b) => {
        if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1
        return (b.lastActivity || 0) - (a.lastActivity || 0)
      })
  }

  getSummary (roomId) {
    return this.summaries.get(roomId) || null
  }

  async _saveSummary (record) {
    const before = this.summaries.get(record.id)
    this.summaries.set(record.id, record)
    await this.local.put('room/' + record.id, record)

    // Only republish when something the directory actually carries moved.
    // Every message touches a summary — unread counts, previews, timestamps —
    // and none of that is the server's business or worth the round trip.
    if (directoryFieldsChanged(before, record)) this.publishDirectory()

    this.emit('rooms-changed')
    return record
  }

  /**
   * Create a room we own. `type` decides whether this behaves like a group,
   * a broadcast channel, a one-to-one chat or a private notes space.
   */
  async createRoom ({ name = '', description = '', type = ROOM_TYPE.GROUP, settings = {} } = {}) {
    // Saved messages is not a chat with anybody, and a frozen account keeps
    // its notes like everything else it already had.
    if (type !== ROOM_TYPE.SAVED) await this._assertMayStartChats()

    // A channel is a broadcast: read-only for everyone but its admins from the
    // first moment, not something the owner has to remember to switch on after
    // creating it. The caller can still override this explicitly.
    const roomSettings = { ...settings }
    if (type === ROOM_TYPE.CHANNEL && roomSettings.readOnly === undefined) {
      roomSettings.readOnly = true
    }

    const encryptionKey = randomBytes(32)
    const room = new Room(this, { encryptionKey })
    await room.ready()
    await room.initialise({ name, description, type, settings: roomSettings })

    this.rooms.set(room.id, room)
    this._wireRoom(room)

    await this._saveSummary({
      id: room.id,
      key: b4a.toString(room.key, 'hex'),
      encryptionKey: b4a.toString(room.encryptionKey, 'hex'),
      type,
      name,
      pinned: false,
      muted: false,
      archived: false,
      joinedAt: clockNow(),
      lastActivity: clockNow(),
      unread: 0
    })

    this.emit('room-added', room.id)
    return room
  }

  /** Notes-to-self. Created lazily, one per account. */
  async savedMessages () {
    const existing = this.listRooms().find((r) => r.type === ROOM_TYPE.SAVED)
    if (existing) return this.openRoom(existing.id)
    return this.createRoom({ name: 'Saved messages', type: ROOM_TYPE.SAVED })
  }

  async openRoom (roomId) {
    // A room goes into the map before it has finished opening, so that two
    // callers cannot build two Autobases over the same cores. The second one
    // therefore has to wait for the first: handing back a room whose view is
    // still null is how a chat opened by the background restore and by a tap
    // at the same moment came out half-built and reported itself unwritable.
    const cached = this.rooms.get(roomId)
    if (cached) {
      await cached.ready()
      return cached
    }

    const record = this.summaries.get(roomId)
    if (!record) throw new Error('Unknown room: ' + roomId)
    if (record.placeholder) {
      // The directory knows its name and who is in it; the key is not the
      // server's to give. restoreRoom() asks the members for it.
      throw new Error('This chat has not been restored yet')
    }

    const room = new Room(this, {
      key: b4a.from(record.key, 'hex'),
      encryptionKey: b4a.from(record.encryptionKey, 'hex'),
      localId: record.id
    })
    this.rooms.set(roomId, room)
    await room.ready()
    this._wireRoom(room)
    room.announceSelf().catch(() => {})
    return room
  }

  /** Open every non-archived room so unread counts stay live in the chat list. */
  async syncAll ({ limit = 40 } = {}) {
    const list = this.listRooms().filter((r) => !r.archived && !r.placeholder).slice(0, limit)
    await Promise.all(list.map((r) => this.openRoom(r.id).catch(() => null)))
    await this.restoreInviteHosts()
    // Once the chats have had a moment to find their people, wake the ones
    // who did not turn up: what they said while this device was off is on
    // their phones only. See lib/wakes.js.
    this._pullAfter(8000)
  }

  _assistantSoon (ms) {
    if (this._assistantTimer || this.closing) return
    this._assistantTimer = setTimeout(() => {
      this._assistantTimer = null
      if (!this.closing && this.onboarded) this.assistant.ensureChat().catch(() => {})
    }, ms)
    if (this._assistantTimer.unref) this._assistantTimer.unref()
  }

  _pullAfter (ms) {
    if (this._pullTimer || this.closing) return
    this._pullTimer = setTimeout(() => {
      this._pullTimer = null
      if (!this.closing) this.pullRecent()
    }, ms)
    if (this._pullTimer.unref) this._pullTimer.unref()
  }

  _wireRoom (room) {
    // A room this device cannot write in yet: ask the room itself to let
    // it in, and keep what is written meanwhile (Room.seekAdmission). The
    // inbox route below (restoreRoom) stays as the fallback.
    if (!room.writable) room.seekAdmission()
    room.on('admitted', () => this.emit('rooms-changed'))

    /*
     * Put our name and photo into this room, once, as soon as we can write.
     *
     * updateProfile() only reaches rooms that happen to be open at the time,
     * so a photo set while a chat was closed never travelled: the other side
     * kept showing initials and there was nothing to do about it short of
     * changing the picture again with every chat open. announceSelf() writes
     * nothing when nothing has changed, so doing it on open costs a lookup.
     */
    let announced = false

    /*
     * The other side saying who they are, live.
     *
     * The room's own member record is the durable answer and arrives whenever
     * the append replicates. This is the same news a second earlier, off the
     * ephemeral bus, and it is what makes a name or a photo changed on one
     * phone appear on the other one while both people are looking at the chat.
     *
     * It is signed by the account it describes (see ephemeral.js), and it can
     * only ever rewrite that account's own card — so a member of a group
     * cannot use it to rename anybody but themselves.
     */
    room.on('peer-profile', async (account, card) => {
      if (!card || !account || account === this.identity.id) return

      const name = cleanName(card.name || '')
      const avatar = typeof card.avatar === 'string' && card.avatar ? card.avatar : null

      const contact = await this.getContact(account).catch(() => null)
      const patch = {}
      if (name && name !== (contact && contact.name)) patch.name = name
      if (avatar && avatar !== (contact && contact.avatar)) patch.avatar = avatar
      if (card.username && card.username !== (contact && contact.username)) patch.username = card.username
      // A bot says what it does and which commands it takes; the chat with it
      // shows them. Kept on the contact, so it is there before it next speaks.
      const bot = card.bot ? normalizeBotCard(card.bot) : null
      if (JSON.stringify(bot) !== JSON.stringify((contact && contact.bot) || null)) patch.bot = bot
      if (Object.keys(patch).length) await this.saveContact(account, patch).catch(() => {})

      // A one-to-one chat is shown as the person, so their card is its title.
      const record = this.summaries.get(room.id)
      if (!record || record.type !== ROOM_TYPE.DM || record.peer !== account) return
      if ((!name || record.name === name) && (!avatar || record.avatar === avatar)) return
      await this._saveSummary({
        ...record,
        name: name || record.name,
        avatar: avatar || record.avatar || null
      }).catch(() => {})
    })

    room.on('update', async () => {
      // The room may only have become writable on this update — after
      // pairing, the host's WRITER_ADD has to replicate back first.
      if (!announced && room.writable) {
        announced = true
        room.announceSelf().catch(() => { announced = false })
      }
      try {
        const summary = await room.view.summary(this.identity.id)
        const record = this.summaries.get(room.id)
        if (record && summary.meta) {
          const type = summary.meta.type ?? record.type

          // A one-to-one chat is shown as the other person, not as the room's
          // own "Direct message" meta: their name and avatar as they announced
          // them, falling back to whatever the contact card holds.
          let name = summary.meta.name || record.name
          let avatar = summary.meta.avatar || record.avatar || null
          let peer = record.peer

          if (type === ROOM_TYPE.DM) {
            // The side that joined by invite never recorded who the chat is
            // with. Derive it from the membership — in a DM that is simply the
            // member who is not me — so both ends name it after each other.
            if (!peer) {
              const members = await room.view.members().catch(() => [])
              const other = members.find((m) => m.account !== this.identity.id)
              if (other) peer = other.account
            }

            // Room metadata is shared, so for a chat between two people it
            // holds one of the two names and is the wrong one for whoever it
            // does not belong to. Early builds put the invited person's name
            // there and they opened the chat to find themselves greeted by
            // their own name. Those rooms still carry it, so the name is
            // rebuilt here from the person rather than from the room.
            const member = peer ? await room.view.member(peer).catch(() => null) : null
            const contact = peer ? await this.getContact(peer).catch(() => null) : null

            name = (member && member.name) || (contact && contact.name) ||
              (record.peer && record.name !== summary.meta.name ? record.name : '') ||
              (peer ? shortId(peer) : '')
            avatar = (member && member.avatar) || (contact && contact.avatar) || record.avatar || null
          }

          // A blocked person's chat does not light up for what they write:
          // its preview stays on the last thing from before, and its count
          // can only go down. The count used to be held only while their
          // message was the newest line — any line after it (their profile,
          // a missed call) brought every one of their messages back as unread.
          const blockedPeer = type === ROOM_TYPE.DM && peer && this.isBlocked(peer)
          const muffled = blockedPeer &&
            summary.last && summary.last.author === peer && summary.last.timestamp > this.blockedSince(peer)

          await this._saveSummary({
            ...record,
            peer,
            name,
            type: summary.meta.type ?? record.type,
            avatar,
            memberCount: summary.memberCount,
            unread: blockedPeer ? Math.min(summary.unread, record.unread || 0) : summary.unread,
            lastActivity: summary.last && !muffled ? summary.last.timestamp : record.lastActivity,
            lastPreview: summary.last && !muffled ? previewOf(summary.last) : record.lastPreview,
            // A call has no text of its own to preview; the list words it
            // in the app's language, and needs to know whose call it was.
            lastCall: summary.last && !muffled ? callOf(summary.last, this.identity.id) : (record.lastCall || null)
          })
        }
        this.emit('room-update', room.id, summary)

        if (summary.last && summary.last.author !== this.identity.id && !this.isBlocked(summary.last.author)) {
          const record2 = this.summaries.get(room.id)
          if (record2 && !record2.muted && summary.unread > 0) {
            this.emit('notification', {
              roomId: room.id,
              roomName: (summary.meta && summary.meta.name) || 'Pine',
              message: summary.last,
              unread: summary.unread
            })
          }
        }
      } catch { /* room closed mid-update */ }
    })

    room.on('update', () => this._prefetchMedia(room))
    this._prefetchMedia(room)

    room.on('typing', (accounts) => this.emit('typing', room.id, accounts))
    room.on('call-signal', (signal) => this.emit('call-signal', room.id, signal))
    room.on('error', (err) => this.emit('error', err))
  }

  /**
   * Fetch the photos, videos and voice notes of new messages as they arrive.
   *
   * A message and its media travel differently. The message is an op in the
   * room, and replicates the moment the two devices meet. The media is a
   * separate core, and it used to be fetched only when somebody opened the
   * chat and looked at it - by which time, in a conversation between two
   * phones, the sender had usually put theirs away. Nobody else has the
   * bytes, so the photo never came: the message said a photo had been sent,
   * and the place where it should have been said "nobody online has it".
   *
   * So the blocks are asked for while the sender is still there, which is
   * exactly when the message arrived. The "Auto-download media" switch in
   * Settings existed all along and was read by nothing; this is what it now
   * controls. Large files are left for a tap.
   */
  _prefetchMedia (room) {
    if (this.settings.autoDownloadMedia === false) return
    if (!room || room.closed || !room.blobs || !room.view) return
    const now = Date.now()
    const wait = (room._prefetchAt || 0) + PREFETCH_INTERVAL - now
    if (wait > 0) {
      // Too soon after the last look — but the update that brought us here
      // may be the very one carrying the photo. Dropping it lost exactly that:
      // a message landing within a few seconds of the room opening, with
      // nothing after it to trigger another look. So look again once the
      // window is over.
      if (!room._prefetchTimer) {
        room._prefetchTimer = setTimeout(() => {
          room._prefetchTimer = null
          this._prefetchMedia(room)
        }, wait)
        if (room._prefetchTimer.unref) room._prefetchTimer.unref()
      }
      return
    }
    room._prefetchAt = now

    room.view.messages({ limit: PREFETCH_WINDOW }).then((list) => {
      for (const message of list) {
        if (!message || message.deleted || message.author === this.identity.id) continue
        if (!MEDIA_KINDS.has(message.kind)) continue
        for (const attachment of message.attachments || []) {
          if (!attachment || !attachment.blobId) continue
          if ((attachment.size || 0) > PREFETCH_MAX_BYTES) continue
          room.blobs.prefetch(attachment).catch(() => {})
        }
      }
    }).catch(() => {})
  }

  async setRoomFlags (roomId, patch) {
    const record = this.summaries.get(roomId)
    if (!record) return null
    return this._saveSummary({ ...record, ...patch })
  }

  async deleteRoom (roomId, { leave = true } = {}) {
    const record = this.summaries.get(roomId)
    // Leaving Anya's chat is a choice to remember: she is not brought back.
    if (record && record.peer && this.assistant && this.assistant.isAssistant(record.peer)) {
      await this.assistant.left(record.peer)
    }
    const room = this.rooms.get(roomId)
    if (room && leave) await room.leave().catch(() => {})
    else if (room) await room.close().catch(() => {})
    this.rooms.delete(roomId)
    this.summaries.delete(roomId)
    await this.local.del('room/' + roomId)
    // Written down, so merging with the other devices' lists does not put it
    // back (see _publishNow).
    await this.local.put('left/' + roomId, { at: Date.now() }).catch(() => {})
    this.publishDirectory({ delay: 1500 })
    this.emit('rooms-changed')
  }

  /* ------------------------------------------------------------------ */
  /* invites and joining                                                */
  /* ------------------------------------------------------------------ */

  async createInvite (roomId, { expiresInMs = 0, uses = 0, role = 'member' } = {}) {
    const room = await this.openRoom(roomId)
    if (!(await room.can(ACTION.INVITE))) {
      throw new Error('You do not have permission to invite people to this room')
    }
    return this._mintInvite(room, { expiresInMs, uses, role })
  }

  /**
   * An invite without the permission check.
   *
   * Only for re-admitting somebody the room already lists as a member — the
   * room's own log is the authority there, and it is a stronger one than the
   * invite permission, which asks a different question. It has to be separate
   * because a direct chat refuses invites once it has its two people, which is
   * exactly right for keeping strangers out and exactly wrong for letting one
   * of those two back in after they lost their phone.
   */
  async _mintInvite (room, { expiresInMs = 0, uses = 0, role = 'member' } = {}) {
    const roomId = room.id
    const meta = await room.view.meta()
    const invite = Invite.create({
      kind: meta && meta.type === ROOM_TYPE.CHANNEL ? INVITE_KIND.CHANNEL : INVITE_KIND.ROOM,
      hint: meta ? meta.name : '',
      expires: expiresInMs ? Date.now() + expiresInMs : 0,
      uses,
      role
    })

    await this.local.put('invite/' + roomId + '/' + invite.code, {
      roomId,
      ...invite.toJSON()
    })
    await this._startHost(room, invite)
    return invite
  }

  async listInvites (roomId) {
    const out = []
    for await (const node of this.local.createReadStream({
      gte: 'invite/' + roomId + '/',
      lt: 'invite/' + roomId + '0'
    })) {
      out.push(node.value)
    }
    return out
  }

  async revokeInvite (roomId, code) {
    const host = this.hosts.get(code)
    if (host) {
      await host.stop()
      this.hosts.delete(code)
    }
    await this.local.del('invite/' + roomId + '/' + code)
  }

  async _startHost (room, invite) {
    if (this.hosts.has(invite.code)) return this.hosts.get(invite.code)
    const host = new PairingHost(this, room, invite)
    host.on('joined', ({ account, name }) => this.emit('member-joined', room.id, { account, name }))
    host.on('error', (err) => this.emit('error', err))
    await host.start()
    this.hosts.set(invite.code, host)
    return host
  }

  /** Re-announce every live invite after a restart. */
  async restoreInviteHosts () {
    for await (const node of this.local.createReadStream({ gte: 'invite/', lt: 'invite0' })) {
      const record = node.value
      const invite = Invite.fromJSON(record)
      if (invite.expired) continue
      if (!this.summaries.has(record.roomId)) continue
      try {
        const room = await this.openRoom(record.roomId)
        await this._startHost(room, invite)
      } catch { /* room unavailable */ }
    }
  }

  /** Redeem an invite code or link. */
  async joinRoom (codeOrLink, { timeout = 90000, onProgress = null, restoring = false } = {}) {
    if (!restoring) await this._assertMayStartChats()
    const invite = Invite.parse(codeOrLink)
    const guest = new PairingGuest(this, invite)
    if (onProgress) {
      guest.on('paired', () => onProgress('joined'))
      onProgress('searching')
    }
    const room = await guest.pair({ timeout })
    this.emit('room-added', room.id)
    return room
  }

  /** Used by PairingGuest once it has the room key. */
  async _materialiseRoom ({ key, encryptionKey, meta }) {
    const id = z32.encode(key)
    const existing = this.rooms.get(id)
    if (existing) {
      await existing.ready()
      return existing
    }

    const room = new Room(this, { key, encryptionKey, localId: id })
    this.rooms.set(id, room)
    await room.ready()
    this._wireRoom(room)

    const known = this.summaries.get(id)

    // A placeholder from the chat directory is upgraded in place, not skipped.
    // It is the same room under the same id, and it is here precisely because
    // this device had no key for it — writing one in is the whole point of
    // arriving here. Left alone it stayed unopenable, and the chat came back
    // for one session and was gone again at the next launch.
    if (!known || known.placeholder) {
      await this._saveSummary({
        pinned: false,
        muted: false,
        archived: false,
        unread: 0,
        ...(known || {}),
        id,
        key: b4a.toString(key, 'hex'),
        encryptionKey: b4a.toString(encryptionKey, 'hex'),
        placeholder: false,
        type: meta && meta.type != null ? meta.type : (known ? known.type : ROOM_TYPE.GROUP),
        name: (known && known.name) || (meta && meta.name) || '',
        joinedAt: (known && known.joinedAt) || clockNow(),
        lastActivity: clockNow()
      })
    }
    return room
  }

  /* ------------------------------------------------------------------ */
  /* contacts and direct messages                                       */
  /* ------------------------------------------------------------------ */

  async listContacts () {
    const out = []
    for await (const node of this.local.createReadStream({ gte: 'contact/', lt: 'contact0' })) {
      out.push(node.value)
    }
    return out.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }

  async getContact (accountId) {
    const node = await this.local.get('contact/' + accountId)
    return node ? node.value : null
  }

  async saveContact (accountId, patch = {}) {
    const key = 'contact/' + accountId
    const node = await this.local.get(key)
    const record = { account: accountId, ...(node ? node.value : {}), ...patch }
    await this.local.put(key, record)
    this.emit('contacts-changed')
    return record
  }

  /*
   * Blocking somebody.
   *
   * There is no server between two people that could refuse to carry their
   * messages, so a block is kept by this device, and it is what this device
   * stops doing: showing their requests to chat, ringing for their calls,
   * showing their new messages in the chat with them or notifying about them,
   * showing their stories and sending them ours. They are not told. Their
   * app can still write into the shared chat — it is a shared log — but
   * nothing they write after the block is shown here.
   */
  async blockContact (accountId, blocked = true) {
    const at = blocked ? Date.now() : 0
    const record = await this.saveContact(accountId, { blocked: !!blocked, blockedAt: at })
    if (blocked) {
      this.blocked.set(accountId, at)
      for (const letter of this.requests.filter((r) => r.from === accountId)) {
        await this._dropRequest(letter, { declined: true }).catch(() => {})
      }
    } else {
      this.blocked.delete(accountId)
    }
    this.emit('blocked-changed', accountId, !!blocked)
    this.emit('rooms-changed')
    if (this.stories) this.emit('stories', this.stories.items)
    return record
  }

  /** Whether this account is blocked here — and since when. */
  isBlocked (accountId) {
    return !!accountId && this.blocked.has(accountId)
  }

  blockedSince (accountId) {
    return this.blocked.get(accountId) || 0
  }

  async _loadBlocked () {
    this.blocked = new Map()
    for await (const node of this.local.createReadStream({ gte: 'contact/', lt: 'contact0' })) {
      const contact = node.value
      if (contact && contact.blocked && contact.account) this.blocked.set(contact.account, contact.blockedAt || 1)
    }
  }

  /**
   * Invite one account — a person or a bot — into a group or channel, as a
   * request in their list, the way a one-to-one chat starts.
   *
   * One attempt, unlike a chat's outbox: the screen that asked says at once
   * whether it got there. A bot is always running and takes it immediately;
   * a person who is not online can be sent the link instead.
   */
  async inviteToRoom (roomId, account, { timeout = 20000 } = {}) {
    await this._assertReachable(account)
    const invite = await this.createInvite(roomId, { uses: 1 })
    this.wake([account], 'msg')
    try {
      await deliverLetter(this, account, { invite: invite.code, note: GROUP_INVITE }, { timeout })
      return true
    } catch (err) {
      await this.revokeInvite(roomId, invite.code).catch(() => {})
      throw err
    }
  }

  /* ------------------------------------------------------------------ */
  /* bots                                                               */
  /* ------------------------------------------------------------------ */

  /**
   * An account's bot card, or null for a person.
   *
   * What the bot itself last said in a chat with us comes first — it is
   * peer to peer and current. The server's copy covers a bot we have not
   * met yet, and is remembered on the contact once asked for.
   */
  async botCard (account, { ask = true } = {}) {
    if (!account) return null
    const contact = await this.getContact(account).catch(() => null)
    if (contact && contact.bot) return contact.bot
    if (!ask || !this.registry || !this.registry.enabled) return null
    // People far outnumber bots: an answer of "not a bot" is remembered for
    // a while, so opening a chat with a person does not ask every time.
    this._notBots = this._notBots || new Map()
    if (Date.now() - (this._notBots.get(account) || 0) < 10 * 60 * 1000) return null
    const cards = await this.registry.botCards([account]).catch(() => null)
    const card = cards && cards[account] ? normalizeBotCard(cards[account]) : null
    if (!card && cards) this._notBots.set(account, Date.now())
    if (card && contact) await this.saveContact(account, { bot: card }).catch(() => {})
    return card
  }

  /**
   * Publish this account's own card to the server — for the bot runtime
   * (packages/pine-bot). An ordinary account has nothing to publish and
   * withdraws whatever was there.
   */
  async publishBotCard () {
    if (!this.registry || !this.registry.enabled) return false
    const card = this.identity.profile.bot ? normalizeBotCard(this.identity.profile.bot) : null
    return this.registry.publishBot(card)
  }

  /* ------------------------------------------------------------------ */
  /* push wake-ups                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Where this device can be woken. The Android shell hands the token over
   * when Firebase gives it one, and again whenever it changes; see
   * lib/push.js for what a wake is and is not.
   */
  async setPushToken (token, platform = 'android') {
    if (!token) return null
    this._pushToken = token
    this._pushPlatform = platform
    if (this.settings.push === false) return null
    // Ringing on the push itself is on unless switched off (Settings).
    return this.push.register(token, { platform, ring: platform === 'android' && this.settings.ringFromPush !== false })
  }

  /** Stop this device being woken — the switch in Settings. */
  async dropPushToken () {
    if (!this._pushToken) return null
    return this.push.unregister(this._pushToken)
  }

  /**
   * Wake other people's devices. Called for a call as it starts, and after a
   * message for anyone in the room who is not connected right now.
   */
  wake (accounts, kind = 'msg', extra = {}) {
    const others = (accounts || []).filter((a) => a && a !== this.identity.id)
    if (!others.length || !this.push) return
    this.push.wake(others, kind, extra).catch(() => {})
  }

  /**
   * A message went out in this room: make sure everybody else's device is
   * running to receive it. See lib/wakes.js for how, and why it is no longer
   * "only the ones who look offline, at most once in twenty seconds".
   */
  async _wakeForMessage (room) {
    if (this.wakes) await this.wakes.messageSent(room)
  }

  /**
   * This chat was opened and may be missing what was said while this device
   * was away. If nobody else is here within a few seconds, their devices are
   * asked to wake and hand it over.
   */
  pullMissed (roomId) {
    const room = this.rooms.get(roomId)
    if (room && this.wakes) this.wakes.pullSoon(room)
  }

  /** The same for the most recent chats — on launch, and on coming back. */
  pullRecent () {
    if (!this.wakes || !this.onboarded) return Promise.resolve([])
    return this.wakes.pullRecent().catch(() => [])
  }

  /* ------------------------------------------------------------------ */
  /* reporting                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Report one message in a channel.
   *
   * Only that message travels. A channel post is public to everybody in the
   * channel already, so sending it to an operator gives away nothing that the
   * audience does not have — which is exactly why this is the one thing that
   * can be reported without sending a conversation along with it.
   */
  async reportMessage ({ roomId, messageId, reason = 'other', note = '' }) {
    if (!this.support || !this.support.enabled) return false

    const room = await this.openRoom(roomId)
    const message = await room.view.message(messageId).catch(() => null)
    if (!message) return false

    const member = await room.view.member(message.author).catch(() => null)
    return this.support.send({
      kind: 'message',
      target: message.author,
      room: roomId,
      reason,
      note,
      transcript: transcriptFrom([message], { [message.author]: (member && member.name) || '' })
    })
  }

  /**
   * Report a person, with the conversation attached.
   *
   * The conversation is the point: a single line proves nothing, and an
   * operator asked to act on "they were abusive, trust me" can only shrug.
   * It is also the reason the screen that calls this has to say, in plain
   * words, that the chat is about to be uploaded — see lib/reports.js.
   */
  async reportUser ({ account, roomId = '', reason = 'other', note = '' }) {
    if (!this.support || !this.support.enabled) return false

    let transcript = []
    if (roomId) {
      const room = await this.openRoom(roomId).catch(() => null)
      if (room) {
        const [messages, members] = await Promise.all([
          room.view.messages({ limit: 100 }).catch(() => []),
          room.view.members().catch(() => [])
        ])
        const names = {}
        for (const member of members) names[member.account] = member.name || ''
        transcript = transcriptFrom(messages, names)
      }
    }

    return this.support.send({
      kind: 'user',
      target: account,
      room: roomId,
      reason,
      note,
      transcript
    })
  }

  /**
   * Open the one-to-one chat with somebody, creating it the first time.
   *
   * Returns as soon as the room exists locally. Whether the other person has
   * received their invite yet is reported separately and settled in the
   * background — see lib/directs.js for why that is not the caller's problem.
   *
   * Calling this twice for the same person opens the same room twice.
   */
  async startDirectMessage (accountId, { name = '' } = {}) {
    // A chat that already exists is not a new one, frozen or not: "everything
    // they already have keeps working" includes opening it.
    if (!this.directs.find(accountId)) {
      await this._assertMayStartChats()
      await this._assertReachable(accountId)
    }
    return this.directs.open(accountId, { name })
  }

  /** The chat with an account, if there is one. */
  findDirectMessage (accountId) {
    return this.directs.find(accountId)
  }

  /** Whether that person's invite is still on its way. */
  directPending (accountId) {
    return this.directs.isPending(accountId)
  }

  /** Their invite code, for handing over by some other route. */
  directInvite (accountId) {
    return this.directs.pendingInvite(accountId)
  }

  _onLetter (letter) {
    const note = letter.note || ''

    if (note.startsWith(RESTORE_REQUEST)) {
      this._onRestoreRequest(letter, note.slice(RESTORE_REQUEST.length)).catch(() => {})
      return
    }
    if (!letter.invite) return
    if (this.isBlocked(letter.from)) return

    const known = this.requests.find((r) => r.from === letter.from && r.invite === letter.invite)
    if (known) return

    /*
     * A request is a new chat. From a frozen account it is one the sender's
     * own app should never have sent — so it is not shown; and a frozen
     * account could not accept it anyway, so it is not shown there either.
     */
    this.frozenAmong([letter.from]).then((frozen) => {
      if (frozen.has(letter.from) || this.selfFrozen) return
      return this._keepRequest(letter)
    }).catch(() => this._keepRequest(letter).catch(() => {}))
  }

  /*
   * Requests are written down, not just held in memory.
   *
   * The sender stops resending the moment the letter lands, so a request that
   * lived only in memory was gone for good if the app was closed or the page
   * reloaded before anybody pressed "accept" — which on a phone, where the
   * system reloads a web app whenever it likes, was most of them. From the
   * sender's side the chat said "delivered"; on this side there was nothing.
   * A declined invite is remembered too, so the sender's retries do not bring
   * it back.
   */
  async _loadRequests () {
    this.requests = []
    for await (const node of this.local.createReadStream({ gte: 'request/', lt: 'request0' })) {
      const letter = node.value
      if (!letter || !letter.from || !letter.invite) continue
      // An invite a week old has long expired on the other side as well.
      if (Date.now() - (letter.at || 0) > REQUEST_TTL) {
        await this.local.del(node.key).catch(() => {})
        continue
      }
      this.requests.push(letter)
    }
  }

  async _keepRequest (letter) {
    if (this.requests.find((r) => r.from === letter.from && r.invite === letter.invite)) return
    if (await this.local.get(requestKey('declined', letter)).catch(() => null)) return
    /*
     * Already in that chat — a retry of an invite we took, crossing with our
     * own accept. Recognised by the invite itself, not by "we already have a
     * chat with this person": that rule threw away every *new* chat from
     * somebody we had an old one with — a person who lost their phone and
     * started again, or deleted the chat and wrote anew — and a bot, which
     * never forgets a chat, ignored them for good. The other side stops
     * resending as soon as it sees us in the room, so an invite we took
     * before this record existed comes back at most briefly.
     */
    if (await this.local.get(requestKey('accepted', letter)).catch(() => null)) return
    if (this.requests.find((r) => r.from === letter.from && r.invite === letter.invite)) return
    this.requests.push(letter)
    await this.local.put(requestKey('request', letter), letter).catch(() => {})
    this.emit('request', letter)
  }

  async _dropRequest (letter, { declined = false } = {}) {
    this.requests = this.requests.filter((r) => r.invite !== letter.invite)
    await this.local.del(requestKey('request', letter)).catch(() => {})
    if (declined) await this.local.put(requestKey('declined', letter), { at: Date.now() }).catch(() => {})
    this.emit('requests-changed')
  }

  /**
   * Somebody who lost their device wants back into a room we share.
   *
   * The check that makes this safe is that the room itself already says they
   * belong: their account is in its member list, written there by ops signed
   * with that same account key. So this grants nothing new — it hands back
   * access that was already theirs, to a request signed by the account that
   * holds it. An account that is not a member gets silence.
   */
  async _onRestoreRequest (letter, rest) {
    const [roomId, code] = String(rest || '').trim().split(/\s+/)
    if (!roomId || !code) return

    const record = this.summaries.get(roomId)
    if (!record || record.placeholder || record.deleted) return

    const room = await this.openRoom(roomId).catch(() => null)
    if (!room) return
    // Only a device that can write can let another one in. This also keeps
    // the device that is asking — it hears its own account's letters too —
    // from answering itself.
    if (!room.writable) return

    const member = await room.view.member(letter.from).catch(() => null)
    if (!member) return

    // Not createInvite(): a direct chat refuses invites once both people are
    // in it, and the person asking is one of those two.
    const invite = await this._mintInvite(room, { uses: 1 }).catch(() => null)
    if (!invite) return

    // Straight back to the device that asked, sealed to the key it minted for
    // this one exchange.
    await sendSealed(this, code, JSON.stringify({ invite: invite.code }), { timeout: 25000 })
      .catch((err) => { if (process.env.PINE_DEBUG) console.log('[restore reply failed]', err.message) })
  }

  /** The answer: an invite back into a room we only had the name of. */
  async _onRestoreReply (roomId, body, done) {
    const record = this.summaries.get(roomId)
    if (!record || !body) return done()

    let invite = ''
    try {
      invite = JSON.parse(body).invite || ''
    } catch { return done() }
    if (!invite) return done()

    try {
      // For a placeholder this fetches the key; for a room already open from
      // the vault it is the pairing itself that matters, because the host
      // authorises this device's writer core on the way through.
      const room = await this.joinRoom(invite, { restoring: true })
      // joinRoom writes a fresh summary under the room's own id. Carry over
      // what the directory knew, so the chat does not lose its name in the
      // moment it comes back.
      const joined = this.summaries.get(room.id)
      if (joined) {
        await this._saveSummary({
          ...joined,
          name: joined.name || record.name || '',
          members: (joined.members && joined.members.length ? joined.members : record.members) || []
        })
      }
      if (room.id !== roomId) await this._forgetPlaceholder(roomId)
      this.emit('room-restored', room.id)
    } catch (err) {
      if (process.env.PINE_DEBUG) console.log('[restore join failed]', err && err.message)
      /* the invite did not take; the button can be pressed again */
    }
    done()
  }

  async _forgetPlaceholder (roomId) {
    this.summaries.delete(roomId)
    await this.local.del('room/' + roomId).catch(() => {})
  }

  async acceptRequest (letter) {
    await this._assertReachable(letter.from)
    // Written before the pairing: the sender keeps resending until it sees
    // us in the room, and a copy landing mid-accept must not come back as a
    // request of its own (see _keepRequest).
    await this.local.put(requestKey('accepted', letter), { at: Date.now() }).catch(() => {})
    let room
    try {
      room = await this.joinRoom(letter.invite)
    } catch (err) {
      await this.local.del(requestKey('accepted', letter)).catch(() => {})
      throw err
    }
    await this.saveContact(letter.from, { name: letter.name })

    // Record who this chat is with straight away, so it is named after the
    // sender from the first frame rather than showing "Direct message" until
    // the first membership update lands.
    const meta = await room.view.meta().catch(() => null)
    if (meta && meta.type === ROOM_TYPE.DM) {
      const record = this.summaries.get(room.id)
      if (record) {
        await this._saveSummary({
          ...record,
          peer: letter.from,
          name: letter.name || record.name
        })
      }
    }

    await this._dropRequest(letter)
    return room
  }

  async declineRequest (letter) {
    await this._dropRequest(letter, { declined: true })
  }

  /* ------------------------------------------------------------------ */
  /* drafts and search                                                  */
  /* ------------------------------------------------------------------ */

  async saveDraft (roomId, text) {
    if (!text) await this.local.del('draft/' + roomId)
    else await this.local.put('draft/' + roomId, { text, at: Date.now() })
  }

  async getDraft (roomId) {
    const node = await this.local.get('draft/' + roomId)
    return node ? node.value.text : ''
  }

  /** Search across every open room. */
  async search (query, { limit = 40 } = {}) {
    const results = []
    for (const room of this.rooms.values()) {
      const hits = await room.view.search(query, { limit })
      for (const m of hits) results.push({ roomId: room.id, message: m })
      if (results.length >= limit) break
    }
    return results
      .sort((a, b) => b.message.timestamp - a.message.timestamp)
      .slice(0, limit)
  }

  get totalUnread () {
    let n = 0
    for (const r of this.summaries.values()) {
      if (r.muted || r.archived) continue
      n += r.unread || 0
    }
    return n
  }
}

/**
 * One-line summary for the chat list.
 *
 * This string is persisted in the local index, and the core has no locale, so
 * it leans on glyphs rather than words. Anything that would need translating
 * is left to the UI, which renders from the message itself.
 */
function previewOf (message) {
  if (!message) return ''
  if (message.deleted) return '—'
  switch (message.kind) {
    case KIND.IMAGE: return '\u{1F5BC} ' + (message.text || '')
    case KIND.VIDEO: return '\u{1F3AC} ' + (message.text || '')
    case KIND.AUDIO: return '\u{1F3A4}'
    case KIND.FILE: return '\u{1F4CE} ' + (message.attachments[0] ? message.attachments[0].name : '')
    case KIND.STICKER: return message.text || '\u{1F642}'
    case KIND.GIF: return 'GIF'
    case KIND.LOCATION: return '\u{1F4CD}'
    case KIND.CONTACT: return '\u{1F464}'
    case KIND.POLL: return '\u{1F4CA} ' + (message.text || '')
    case KIND.SYSTEM: return ''
    default: return message.text || ''
  }
}

/** The call a system line records, as the chat list needs it; null for anything else. */
function callOf (message, me) {
  const extra = message && message.kind === KIND.SYSTEM ? message.extra : null
  if (!extra || extra.event !== 'call') return null
  return { state: extra.state, duration: extra.duration || 0, out: message.author === me }
}

/** Whether a summary change is one the chat directory would notice. */
function directoryFieldsChanged (before, after) {
  if (!before) return true
  return before.name !== after.name ||
    before.type !== after.type ||
    !!before.deleted !== !!after.deleted ||
    !!before.placeholder !== !!after.placeholder ||
    JSON.stringify(before.members || []) !== JSON.stringify(after.members || [])
}

function shortId (id) {
  return id.slice(0, 6) + '…' + id.slice(-4)
}

/** Where a request, or the fact that one was declined, is kept in the local index. */
function requestKey (kind, letter) {
  return kind + '/' + letter.from + '/' + String(letter.invite).slice(-24)
}

/**
 * The other person in a one-to-one chat, when there is exactly one.
 *
 * Used to name a restored DM before its room has replicated far enough to say
 * anything: a chat that comes back titled with a stranger's key fragment is
 * only marginally better than one that comes back with no title at all.
 */
function soleOther (type, members, me) {
  if (type !== ROOM_TYPE.DM) return ''
  const others = (members || []).map((m) => m && m.account).filter((a) => a && a !== me)
  return others.length === 1 ? others[0] : ''
}

export { ROOM_TYPE, ROLE, KIND, ACTION }
